import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Slider from '@react-native-community/slider';
import { Svg, Circle, Text as SvgText } from 'react-native-svg';
import Footer from '../components/Footer';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobile = screenWidth < 768;

const colors = {
  primary: '#4F46E5',
  background: '#FFFFFF',
  surface: '#F8FAFC',
  cardBackground: '#FFFFFF',
  border: '#E2E8F0',
  text: '#1E293B',
  textSecondary: '#64748B',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
};

const CarLoanEMICalculatorScreen = () => {
  const [loanAmount, setLoanAmount] = useState('500000');
  const [downPayment, setDownPayment] = useState('100000');
  const [interestRate, setInterestRate] = useState(8.5);
  const [loanTenure, setLoanTenure] = useState(5);
  const [tenureType, setTenureType] = useState('years');
  const [showFAQ, setShowFAQ] = useState(false);
  const [results, setResults] = useState(null);

  const tenureOptions = [
    { label: 'Years', value: 'years' },
    { label: 'Months', value: 'months' },
  ];

  const calculateEMI = () => {
    const principal = parseFloat(loanAmount) - parseFloat(downPayment);
    const rate = parseFloat(interestRate);
    const tenure = parseFloat(loanTenure);

    if (principal <= 0 || rate <= 0 || tenure <= 0) {
      setResults(null);
      return;
    }

    // Convert tenure to months if in years
    const tenureInMonths = tenureType === 'years' ? tenure * 12 : tenure;
    
    // Monthly interest rate
    const monthlyRate = rate / (12 * 100);
    
    // EMI calculation using formula: P * r * (1+r)^n / ((1+r)^n - 1)
    const emi = (principal * monthlyRate * Math.pow(1 + monthlyRate, tenureInMonths)) / 
                (Math.pow(1 + monthlyRate, tenureInMonths) - 1);
    
    const totalAmount = emi * tenureInMonths;
    const totalInterest = totalAmount - principal;
    const totalCost = totalAmount + parseFloat(downPayment);

    setResults({
      emi: Math.round(emi),
      totalInterest: Math.round(totalInterest),
      totalAmount: Math.round(totalAmount),
      totalCost: Math.round(totalCost),
      principal: Math.round(principal),
      downPayment: parseFloat(downPayment),
    });
  };

  useEffect(() => {
    calculateEMI();
  }, [loanAmount, downPayment, interestRate, loanTenure, tenureType]);

  const renderHeader = () => (
    <View style={styles.header}>
      <View style={styles.headerContent}>
        <Text style={styles.headerTitle}>Car Loan EMI Calculator</Text>
        <Text style={styles.headerSubtitle}>
          Calculate your car loan EMI, total interest, and payment schedule
        </Text>
      </View>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorContainer}>
      <Text style={styles.sectionTitle}>Loan Details</Text>
      
      {/* Car Price */}
      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Car Price (₹)</Text>
        <TextInput
          style={styles.textInput}
          value={loanAmount}
          onChangeText={setLoanAmount}
          placeholder="Enter car price"
          keyboardType="numeric"
        />
      </View>

      {/* Down Payment */}
      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Down Payment (₹)</Text>
        <TextInput
          style={styles.textInput}
          value={downPayment}
          onChangeText={setDownPayment}
          placeholder="Enter down payment"
          keyboardType="numeric"
        />
      </View>

      {/* Interest Rate */}
      <View style={styles.inputGroup}>
        <View style={styles.sliderHeader}>
          <Text style={styles.inputLabel}>Interest Rate</Text>
          <View style={styles.sliderLabels}>
            <Text style={styles.sliderValue}>{interestRate.toFixed(1)}</Text>
            <Text style={styles.sliderUnit}>% per annum</Text>
          </View>
        </View>
        <Slider
          style={styles.slider}
          minimumValue={1}
          maximumValue={20}
          value={interestRate}
          onValueChange={setInterestRate}
          step={0.1}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={{ backgroundColor: colors.primary }}
        />
      </View>

      {/* Loan Tenure */}
      <View style={styles.inputGroup}>
        <View style={styles.sliderHeader}>
          <Text style={styles.inputLabel}>Loan Tenure</Text>
          <View style={styles.sliderLabels}>
            <Text style={styles.sliderValue}>{loanTenure}</Text>
            <Text style={styles.sliderUnit}>{tenureType}</Text>
          </View>
        </View>
        <Slider
          style={styles.slider}
          minimumValue={tenureType === 'years' ? 1 : 12}
          maximumValue={tenureType === 'years' ? 7 : 84}
          value={loanTenure}
          onValueChange={setLoanTenure}
          step={tenureType === 'years' ? 0.5 : 1}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={{ backgroundColor: colors.primary }}
        />
        
        {/* Tenure Type Options */}
        <View style={styles.tenureOptions}>
          {tenureOptions.map((option) => (
            <TouchableOpacity
              key={option.value}
              style={[
                styles.tenureOption,
                tenureType === option.value && styles.tenureOptionSelected,
              ]}
              onPress={() => {
                setTenureType(option.value);
                setLoanTenure(option.value === 'years' ? 5 : 60);
              }}
            >
              <Text
                style={[
                  styles.tenureText,
                  tenureType === option.value && styles.tenureTextSelected,
                ]}
              >
                {option.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </View>
  );

  const renderDonutChart = () => {
    if (!results) return null;

    const { principal, totalInterest, downPayment } = results;
    const total = principal + totalInterest + downPayment;
    
    const principalPercentage = (principal / total) * 100;
    const interestPercentage = (totalInterest / total) * 100;
    const downPaymentPercentage = (downPayment / total) * 100;

    const radius = 50;
    const strokeWidth = 20;
    const circumference = 2 * Math.PI * radius;

    const principalStroke = (principalPercentage / 100) * circumference;
    const interestStroke = (interestPercentage / 100) * circumference;
    const downPaymentStroke = (downPaymentPercentage / 100) * circumference;

    return (
      <View style={styles.chartContainer}>
        <Svg width={120} height={120} style={styles.donutChart}>
          <Circle
            cx={60}
            cy={60}
            r={radius}
            stroke={colors.border}
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          <Circle
            cx={60}
            cy={60}
            r={radius}
            stroke="#10B981"
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={`${downPaymentStroke} ${circumference}`}
            strokeDashoffset={0}
            transform="rotate(-90 60 60)"
          />
          <Circle
            cx={60}
            cy={60}
            r={radius}
            stroke={colors.primary}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={`${principalStroke} ${circumference}`}
            strokeDashoffset={-downPaymentStroke}
            transform="rotate(-90 60 60)"
          />
          <Circle
            cx={60}
            cy={60}
            r={radius}
            stroke="#F59E0B"
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={`${interestStroke} ${circumference}`}
            strokeDashoffset={-(downPaymentStroke + principalStroke)}
            transform="rotate(-90 60 60)"
          />
        </Svg>
        
        <View style={styles.chartLegend}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: '#10B981' }]} />
            <Text style={styles.legendText}>Down Payment</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.primary }]} />
            <Text style={styles.legendText}>Principal</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: '#F59E0B' }]} />
            <Text style={styles.legendText}>Interest</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderResults = () => {
    if (!results) {
      return (
        <View style={styles.emptyResults}>
          <Ionicons name="calculator-outline" size={48} color={colors.textSecondary} />
          <Text style={styles.emptyResultsText}>
            Enter loan details to see EMI calculation
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.resultsContainer}>
        {renderDonutChart()}
        
        <View style={styles.resultsList}>
          <View style={styles.resultRow}>
            <Text style={styles.resultLabel}>Monthly EMI</Text>
            <Text style={styles.resultValue}>₹{results.emi.toLocaleString()}</Text>
          </View>
          
          <View style={styles.resultRow}>
            <Text style={styles.resultLabel}>Principal Amount</Text>
            <Text style={styles.resultValue}>₹{results.principal.toLocaleString()}</Text>
          </View>
          
          <View style={styles.resultRow}>
            <Text style={styles.resultLabel}>Total Interest</Text>
            <Text style={styles.resultValue}>₹{results.totalInterest.toLocaleString()}</Text>
          </View>
          
          <View style={styles.resultRow}>
            <Text style={styles.resultLabel}>Down Payment</Text>
            <Text style={styles.resultValue}>₹{results.downPayment.toLocaleString()}</Text>
          </View>
          
          <View style={[styles.resultRow, styles.totalRow]}>
            <Text style={styles.totalLabel}>Total Cost</Text>
            <Text style={styles.totalValue}>₹{results.totalCost.toLocaleString()}</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderCarLoanInformation = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.infoTitle}>What is Car Loan EMI Calculator?</Text>
      <Text style={styles.infoText}>
        A Car Loan EMI Calculator is a financial tool that helps you calculate the Equated Monthly Installment (EMI) 
        for your car loan. It considers factors like loan amount, interest rate, loan tenure, and down payment to 
        provide accurate EMI calculations and total cost breakdown.
      </Text>

      <Text style={styles.infoSubtitle}>Key Features</Text>
      <View style={styles.bulletList}>
        <Text style={styles.bulletPoint}>• Calculate monthly EMI for car loans</Text>
        <Text style={styles.bulletPoint}>• Factor in down payment amount</Text>
        <Text style={styles.bulletPoint}>• Compare different loan tenures</Text>
        <Text style={styles.bulletPoint}>• Visual breakdown of principal vs interest</Text>
        <Text style={styles.bulletPoint}>• Total cost calculation including down payment</Text>
      </View>

      <Text style={styles.infoSubtitle}>EMI Calculation Formula</Text>
      <View style={styles.formulaBox}>
        <Text style={styles.formulaText}>EMI = P × r × (1+r)ⁿ / ((1+r)ⁿ - 1)</Text>
        <Text style={styles.formulaText}>Where:</Text>
        <Text style={styles.formulaText}>P = Principal loan amount (Car price - Down payment)</Text>
        <Text style={styles.formulaText}>r = Monthly interest rate</Text>
        <Text style={styles.formulaText}>n = Number of monthly installments</Text>
      </View>

      <Text style={styles.infoSubtitle}>How to Use Car Loan EMI Calculator?</Text>
      <View style={styles.stepsList}>
        <Text style={styles.stepText}>1. Enter the total car price</Text>
        <Text style={styles.stepText}>2. Input your down payment amount</Text>
        <Text style={styles.stepText}>3. Set the annual interest rate offered by your lender</Text>
        <Text style={styles.stepText}>4. Choose your preferred loan tenure (years or months)</Text>
        <Text style={styles.stepText}>5. View your monthly EMI and total cost breakdown</Text>
      </View>

      <Text style={styles.infoSubtitle}>Benefits of Using Car Loan EMI Calculator</Text>
      <View style={styles.bulletList}>
        <Text style={styles.bulletPoint}>• Plan your budget effectively</Text>
        <Text style={styles.bulletPoint}>• Compare different loan options</Text>
        <Text style={styles.bulletPoint}>• Understand the impact of down payment</Text>
        <Text style={styles.bulletPoint}>• Make informed financial decisions</Text>
        <Text style={styles.bulletPoint}>• Save time on manual calculations</Text>
      </View>
    </View>
  );

  const renderSEOContent = () => (
    <View style={styles.seoContainer}>
      <Text style={styles.seoTitle}>Complete Guide to Car Loan EMI Planning & Optimization</Text>
      
      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Understanding Car Loan EMI Structure</Text>
        <Text style={styles.seoText}>
          Car loan EMI consists of principal repayment and interest charges. Unlike personal loans, car loans are secured 
          by the vehicle itself, resulting in lower interest rates. The EMI structure follows a reducing balance method 
          where interest is calculated on the outstanding principal amount, making early payments more beneficial for 
          overall interest savings.
        </Text>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Key Calculator Features & Benefits</Text>
        <View style={styles.seoFeatureGrid}>
          <View style={styles.seoFeatureItem}>
            <Text style={styles.seoFeatureTitle}>Down Payment Impact</Text>
            <Text style={styles.seoFeatureText}>Calculate how different down payment amounts affect your EMI and total interest</Text>
          </View>
          <View style={styles.seoFeatureItem}>
            <Text style={styles.seoFeatureTitle}>Tenure Optimization</Text>
            <Text style={styles.seoFeatureText}>Compare EMI amounts across different loan tenures to find your optimal repayment period</Text>
          </View>
          <View style={styles.seoFeatureItem}>
            <Text style={styles.seoFeatureTitle}>Interest Rate Comparison</Text>
            <Text style={styles.seoFeatureText}>Evaluate offers from different lenders to secure the best interest rates</Text>
          </View>
          <View style={styles.seoFeatureItem}>
            <Text style={styles.seoFeatureTitle}>Total Cost Analysis</Text>
            <Text style={styles.seoFeatureText}>Understand the complete financial commitment including down payment and interest</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Car Loan vs Other Vehicle Financing Options</Text>
        <View style={styles.seoComparisonTable}>
          <View style={styles.seoTableHeader}>
            <Text style={styles.seoTableHeaderText}>Financing Type</Text>
            <Text style={styles.seoTableHeaderText}>Interest Rate</Text>
            <Text style={styles.seoTableHeaderText}>Down Payment</Text>
            <Text style={styles.seoTableHeaderText}>Tenure</Text>
          </View>
          <View style={styles.seoTableRow}>
            <Text style={styles.seoTableCell}>Car Loan</Text>
            <Text style={styles.seoTableCell}>7.5% - 12%</Text>
            <Text style={styles.seoTableCell}>10% - 20%</Text>
            <Text style={styles.seoTableCell}>1-7 years</Text>
          </View>
          <View style={styles.seoTableRow}>
            <Text style={styles.seoTableCell}>Personal Loan</Text>
            <Text style={styles.seoTableCell}>10% - 18%</Text>
            <Text style={styles.seoTableCell}>0%</Text>
            <Text style={styles.seoTableCell}>1-5 years</Text>
          </View>
          <View style={styles.seoTableRow}>
            <Text style={styles.seoTableCell}>Lease Financing</Text>
            <Text style={styles.seoTableCell}>8% - 14%</Text>
            <Text style={styles.seoTableCell}>0% - 10%</Text>
            <Text style={styles.seoTableCell}>2-4 years</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Step-by-Step Car Loan EMI Calculation Process</Text>
        <View style={styles.seoStepsList}>
          <View style={styles.seoStep}>
            <Text style={styles.seoStepNumber}>1</Text>
            <Text style={styles.seoStepText}>Determine your car budget and select the vehicle price range</Text>
          </View>
          <View style={styles.seoStep}>
            <Text style={styles.seoStepNumber}>2</Text>
            <Text style={styles.seoStepText}>Calculate affordable down payment (typically 15-25% of car value)</Text>
          </View>
          <View style={styles.seoStep}>
            <Text style={styles.seoStepNumber}>3</Text>
            <Text style={styles.seoStepText}>Research and compare interest rates from multiple lenders</Text>
          </View>
          <View style={styles.seoStep}>
            <Text style={styles.seoStepNumber}>4</Text>
            <Text style={styles.seoStepText}>Choose optimal loan tenure balancing EMI affordability and total interest</Text>
          </View>
          <View style={styles.seoStep}>
            <Text style={styles.seoStepNumber}>5</Text>
            <Text style={styles.seoStepText}>Factor in additional costs like insurance, registration, and processing fees</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Smart Car Loan Optimization Strategies</Text>
        <View style={styles.seoStrategiesList}>
          <View style={styles.seoStrategy}>
            <Text style={styles.seoStrategyTitle}>Maximize Down Payment</Text>
            <Text style={styles.seoStrategyText}>Higher down payment reduces loan amount, EMI, and total interest significantly</Text>
          </View>
          <View style={styles.seoStrategy}>
            <Text style={styles.seoStrategyTitle}>Choose Shorter Tenure</Text>
            <Text style={styles.seoStrategyText}>Opt for shorter tenure if affordable to minimize total interest outgo</Text>
          </View>
          <View style={styles.seoStrategy}>
            <Text style={styles.seoStrategyTitle}>Prepayment Strategy</Text>
            <Text style={styles.seoStrategyText}>Make partial prepayments during initial years for maximum interest savings</Text>
          </View>
          <View style={styles.seoStrategy}>
            <Text style={styles.seoStrategyTitle}>Rate Negotiation</Text>
            <Text style={styles.seoStrategyText}>Leverage good credit score and existing banking relationships for better rates</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Legal & Documentation Requirements</Text>
        <Text style={styles.seoText}>
          Car loan documentation includes identity proof, address proof, income documents, bank statements, 
          and vehicle-related papers. Ensure all documents are current and accurate. The vehicle serves as 
          collateral, and the lender holds the RC (Registration Certificate) until loan completion. 
          Understand your rights regarding insurance requirements and prepayment charges.
        </Text>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Common Car Loan Mistakes to Avoid</Text>
        <View style={styles.seoMistakesList}>
          <Text style={styles.seoMistakeItem}>• Focusing only on EMI amount without considering total interest cost</Text>
          <Text style={styles.seoMistakeItem}>• Not comparing offers from multiple lenders and dealers</Text>
          <Text style={styles.seoMistakeItem}>• Ignoring additional costs like insurance, maintenance, and fuel</Text>
          <Text style={styles.seoMistakeItem}>• Choosing maximum tenure without considering prepayment options</Text>
          <Text style={styles.seoMistakeItem}>• Not reading loan agreement terms and conditions carefully</Text>
          <Text style={styles.seoMistakeItem}>• Overlooking the impact of down payment on overall loan cost</Text>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Success Stories & Case Studies</Text>
        <Text style={styles.seoText}>
          <Text style={styles.seoBoldText}>Case Study 1:</Text> Rajesh saved ₹1.2 lakhs in interest by increasing his down payment 
          from 10% to 25% on a ₹8 lakh car loan. {'\n\n'}
          <Text style={styles.seoBoldText}>Case Study 2:</Text> Priya reduced her total interest by ₹80,000 by choosing a 4-year 
          tenure instead of 6 years, despite higher EMI. {'\n\n'}
          <Text style={styles.seoBoldText}>Case Study 3:</Text> Amit negotiated a 0.5% lower interest rate by leveraging his 
          existing relationship with the bank, saving ₹45,000 over the loan tenure.
        </Text>
      </View>
    </View>
  );

  const renderFAQSection = () => (
    <View style={styles.faqContainer}>
      <TouchableOpacity
        style={styles.faqHeader}
        onPress={() => setShowFAQ(!showFAQ)}
      >
        <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
        <Ionicons
          name={showFAQ ? 'chevron-up' : 'chevron-down'}
          size={24}
          color={colors.text}
        />
      </TouchableOpacity>
      
      {showFAQ && (
        <View style={styles.faqContent}>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What is the ideal down payment for a car loan?</Text>
            <Text style={styles.faqAnswer}>
              Typically, a down payment of 15-25% of the car's value is recommended. A higher down payment 
              reduces your loan amount, monthly EMI, and total interest paid.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>How does loan tenure affect my EMI?</Text>
            <Text style={styles.faqAnswer}>
              Longer tenure reduces monthly EMI but increases total interest paid. Shorter tenure means 
              higher EMI but lower total cost. Choose based on your monthly budget and financial goals.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Can I prepay my car loan?</Text>
            <Text style={styles.faqAnswer}>
              Yes, most lenders allow prepayment of car loans. Some may charge prepayment penalties, 
              especially in the initial years. Check with your lender for specific terms.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What factors affect car loan interest rates?</Text>
            <Text style={styles.faqAnswer}>
              Interest rates depend on your credit score, income, employment history, loan amount, 
              car model (new/used), and the lender's policies. Better credit scores typically get lower rates.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Is car loan interest tax deductible?</Text>
            <Text style={styles.faqAnswer}>
              Car loan interest is generally not tax deductible for personal use vehicles. However, 
              if the car is used for business purposes, you may be eligible for tax benefits.
            </Text>
          </View>
        </View>
      )}
    </View>
  );

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      backgroundColor: colors.primary,
      paddingVertical: 24,
      paddingHorizontal: 20,
    },
    headerContent: {
      maxWidth: 1200,
      alignSelf: 'center',
      width: '100%',
    },
    headerTitle: {
      fontSize: 28,
      fontWeight: 'bold',
      color: '#FFFFFF',
      marginBottom: 8,
    },
    headerSubtitle: {
      fontSize: 16,
      color: '#E0E7FF',
      opacity: 0.9,
    },
    mainContent: {
      flexDirection: (isWeb && !isMobile) ? 'row' : 'column',
      minHeight: (isWeb && !isMobile) ? 600 : 'auto',
    },
    leftPanel: {
      flex: (isWeb && !isMobile) ? 1 : 1,
      backgroundColor: colors.background,
    },
    rightPanel: {
      width: (isWeb && !isMobile) ? 400 : '100%',
      backgroundColor: colors.surface,
      borderLeftWidth: (isWeb && !isMobile) ? 1 : 0,
      borderLeftColor: colors.border,
      padding: 20,
    },
    calculatorContainer: {
      padding: 20,
    },
    sectionTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 20,
    },
    inputGroup: {
      marginBottom: 24,
    },
    inputLabel: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    textInput: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: 12,
      fontSize: 16,
      color: colors.text,
      backgroundColor: colors.background,
    },
    sliderHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 8,
    },
    slider: {
      width: '100%',
      height: 40,
    },
    sliderLabels: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    sliderValue: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    sliderUnit: {
      fontSize: 16,
      color: colors.textSecondary,
      marginLeft: 4,
    },
    tenureOptions: {
      flexDirection: 'row',
      gap: 8,
      marginTop: 8,
    },
    tenureOption: {
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 6,
      backgroundColor: colors.background,
    },
    tenureOptionSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '20',
    },
    tenureText: {
      fontSize: 14,
      color: colors.text,
    },
    tenureTextSelected: {
      color: colors.primary,
      fontWeight: '600',
    },
    resultsContainer: {
      flex: 1,
    },
    emptyResults: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 40,
    },
    emptyResultsText: {
      fontSize: 16,
      color: colors.textSecondary,
      marginTop: 12,
      textAlign: 'center',
    },
    chartContainer: {
      alignItems: 'center',
      marginBottom: 24,
    },
    donutChart: {
      marginBottom: 16,
    },
    chartLegend: {
      flexDirection: 'row',
      gap: 16,
      flexWrap: 'wrap',
      justifyContent: 'center',
    },
    legendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    legendColor: {
      width: 12,
      height: 12,
      borderRadius: 6,
    },
    legendText: {
      fontSize: 14,
      color: colors.text,
    },
    resultsList: {
      gap: 12,
    },
    resultRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 8,
    },
    resultLabel: {
      fontSize: 16,
      color: colors.textSecondary,
    },
    resultValue: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    totalRow: {
      borderTopWidth: 1,
      borderTopColor: colors.border,
      paddingTop: 12,
      marginTop: 8,
    },
    totalLabel: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.text,
    },
    totalValue: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.primary,
    },
    infoContainer: {
      margin: 20,
      padding: 20,
      backgroundColor: colors.surface,
      borderRadius: 12,
      elevation: 2,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
    },
    infoTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 16,
    },
    infoSubtitle: {
      fontSize: 20,
      fontWeight: '600',
      color: colors.text,
      marginTop: 20,
      marginBottom: 12,
    },
    infoText: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
      marginBottom: 12,
    },
    bulletList: {
      marginLeft: 16,
      marginBottom: 16,
    },
    bulletPoint: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
      marginBottom: 4,
    },
    formulaBox: {
      backgroundColor: colors.background,
      padding: 16,
      borderRadius: 8,
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
      marginVertical: 12,
    },
    formulaText: {
      fontSize: 16,
      fontFamily: 'monospace',
      color: colors.text,
      marginBottom: 4,
    },
    stepsList: {
      marginLeft: 8,
      marginBottom: 16,
    },
    stepText: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
      marginBottom: 8,
    },
    faqContainer: {
      backgroundColor: colors.cardBackground,
      margin: 20,
      borderRadius: 12,
      overflow: 'hidden',
      elevation: 2,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
    },
    faqHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 20,
      backgroundColor: colors.primary + '10',
    },
    faqTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
    },
    faqContent: {
      padding: 20,
    },
    faqItem: {
      marginBottom: 20,
      paddingBottom: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    faqQuestion: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    faqAnswer: {
      fontSize: 15,
      color: colors.textSecondary,
      lineHeight: 22,
    },
    // SEO Content Styles
    seoContainer: {
      margin: 20,
      padding: 20,
      backgroundColor: colors.surface,
      borderRadius: 12,
      elevation: 2,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
    },
    seoTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 20,
      textAlign: 'center',
    },
    seoSection: {
      marginBottom: 24,
    },
    seoSubtitle: {
      fontSize: 20,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 12,
    },
    seoText: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
      marginBottom: 12,
    },
    seoBoldText: {
      fontWeight: '600',
      color: colors.text,
    },
    seoFeatureGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
    },
    seoFeatureItem: {
      flex: 1,
      minWidth: 250,
      backgroundColor: colors.background,
      padding: 16,
      borderRadius: 8,
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
    },
    seoFeatureTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    seoFeatureText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    seoComparisonTable: {
      backgroundColor: colors.background,
      borderRadius: 8,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: colors.border,
    },
    seoTableHeader: {
      flexDirection: 'row',
      backgroundColor: colors.primary + '20',
      paddingVertical: 12,
      paddingHorizontal: 8,
    },
    seoTableHeaderText: {
      flex: 1,
      fontSize: 14,
      fontWeight: '600',
      color: colors.text,
      textAlign: 'center',
    },
    seoTableRow: {
      flexDirection: 'row',
      paddingVertical: 10,
      paddingHorizontal: 8,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    seoTableCell: {
      flex: 1,
      fontSize: 14,
      color: colors.textSecondary,
      textAlign: 'center',
    },
    seoStepsList: {
      gap: 12,
    },
    seoStep: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
    },
    seoStepNumber: {
      width: 24,
      height: 24,
      borderRadius: 12,
      backgroundColor: colors.primary,
      color: '#FFFFFF',
      fontSize: 14,
      fontWeight: '600',
      textAlign: 'center',
      lineHeight: 24,
    },
    seoStepText: {
      flex: 1,
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
    },
    seoStrategiesList: {
      gap: 16,
    },
    seoStrategy: {
      backgroundColor: colors.background,
      padding: 16,
      borderRadius: 8,
      borderLeftWidth: 4,
      borderLeftColor: colors.success,
    },
    seoStrategyTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    seoStrategyText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    seoMistakesList: {
      gap: 8,
    },
    seoMistakeItem: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
    },
  });

  return (
    <ScrollView style={styles.container}>
      {renderHeader()}
      <View style={styles.mainContent}>
        <View style={styles.leftPanel}>
          {renderCalculator()}
        </View>
        <View style={styles.rightPanel}>
          {renderResults()}
        </View>
      </View>
      {renderCarLoanInformation()}
      {renderSEOContent()}
      {renderFAQSection()}
      <Footer />
    </ScrollView>
  );
};

export default CarLoanEMICalculatorScreen;